import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Grid,
  Snackbar,
  Alert,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { API_URL } from "../../../../Config/api";

const QuestionCard = ({
  label,
  field,
  value,
  onChange,
  options = ["Yes", "No"],
  children,
}) => (
  <Box
    sx={{
      p: 1.5,
      mb: 1.5,
      borderRadius: "8px",
      border: "1px solid #e0e0e0",
      bgcolor: "#ffffff",
      boxSizing: "border-box",
    }}
  >
    <Typography
      variant="body1"
      sx={{ color: "#333", mb: 0.5, fontWeight: 500 }}
    >
      {label}
    </Typography>
    {field && (
      <RadioGroup
        row
        value={value || ""}
        onChange={(e) => onChange(field, e.target.value)}
      >
        {options.map((opt) => (
          <FormControlLabel
            key={opt}
            value={opt}
            control={
              <Radio
                size="small"
                sx={{ color: "#9e9e9e", "&.Mui-checked": { color: "#2CDFAA" } }}
              />
            }
            label={
              <Typography sx={{ color: "#424242", fontSize: "0.95rem" }}>
                {opt}
              </Typography>
            }
            sx={{ mr: 3 }}
          />
        ))}
      </RadioGroup>
    )}
    {children}
  </Box>
);

const Closuretwelve = ({
  evePlanID,
  onClose,
  emt,
  formNo,
  sessions,
  profET,
  profED,
  profST,
  profSD,
}) => {
  const accessToken = localStorage.getItem("token");
  const userID = localStorage.getItem("userID");

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [closureRevalidateRemark, setClosureRevalidateRemark] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    startDate: profSD || "",
    endDate: profED || "",
    startTime: profST || "",
    endTime: profET || "",
    patientStatus: "",
    tt_status: "",
    niv_on: "",
    isConscious: "",
    vitalsStable: "",
    secretions: "",
    stomaStatus: "",
    ttSize: "",
    ttSecured: "",
    cuffPressure: "",
    suctionWorking: "",
    nivMode: "",
    maskFitting: "",
    skinIntact: "",
    complianceHrs: "",
    humidifier: "",
    filterClean: "",
    oxygenLtr: "",
    symptoms: "",
    deepBreathing: "",
    chestPhysio: "",
    spirometry: "",
    deepBreathingRem: "",
    chestPhysioRem: "",
    spirometryRem: "",
  });

  // 👇 DATA PRE-FILL WITH NEW REMARK KEYS
  useEffect(() => {
    if (sessions && sessions.length > 0) {
      const currentSession = sessions.find(
        (item) => item.dtl_eve_id === evePlanID,
      );

      if (currentSession) {
        const data = currentSession;

        let pStatus = "";
        if (data.tt_tracheostomy_patient === 1) pStatus = "TT";
        else if (data.niv_patient_on_niv === 1) pStatus = "NIV";
        else if (
          data.tt_tracheostomy_patient === 2 &&
          data.niv_patient_on_niv === 2
        )
          pStatus = "None";

        setFormData({
          startDate: data.actual_sdate || data.sess_s_date || profSD || "",
          endDate: data.actual_edate || data.sess_e_date || profED || "",
          startTime: data.actual_stime || data.sess_s_time || profST || "",
          endTime: data.actual_etime || data.sess_e_time || profET || "",

          patientStatus: pStatus,

          // TT Fields
          tt_status:
            data.tt_tracheostomy_patient === 1
              ? "Yes"
              : data.tt_tracheostomy_patient === 2
                ? "No"
                : "",
          secretions:
            data.tt_secretions === 1
              ? "Mild"
              : data.tt_secretions === 2
                ? "Moderate"
                : data.tt_secretions === 3
                  ? "Copious"
                  : "",
          stomaStatus:
            data.tt_stoma === 1
              ? "Healthy"
              : data.tt_stoma === 2
                ? "Redness"
                : data.tt_stoma === 3
                  ? "Discharge present"
                  : "",
          ttSize: data.tt_tube_remark || "",
          ttSecured:
            data.tt_tube_cuff === 1
              ? "Yes"
              : data.tt_tube_cuff === 2
                ? "No"
                : "",
          cuffPressure:
            data.tt_tube_cuff_pressure_checked === 1
              ? "Yes"
              : data.tt_tube_cuff_pressure_checked === 2
                ? "No"
                : "",
          suctionWorking:
            data.tt_suction_system_working === 1
              ? "Yes"
              : data.tt_suction_system_working === 2
                ? "No"
                : "",

          // NIV Fields
          niv_on:
            data.niv_patient_on_niv === 1
              ? "Yes"
              : data.niv_patient_on_niv === 2
                ? "No"
                : "",
          nivMode:
            data.niv_mode === 1 ? "CPAP" : data.niv_mode === 2 ? "BiPAP" : "",
          maskFitting:
            data.niv_mask_fitting === 1
              ? "Adequate"
              : data.niv_mask_fitting === 2
                ? "Leak present"
                : "",
          skinIntact:
            data.niv_skin_over_nasal_bridge_intact_noted === 1
              ? "Yes"
              : data.niv_skin_over_nasal_bridge_intact_noted === 2
                ? "No"
                : "",
          complianceHrs: data.niv_compliance_hours || "",
          humidifier:
            data.niv_humidifier_functioning === 1
              ? "Yes"
              : data.niv_humidifier_functioning === 2
                ? "No"
                : "",
          filterClean:
            data.niv_filter_clean === 1
              ? "Yes"
              : data.niv_filter_clean === 2
                ? "No"
                : "",
          oxygenLtr: data.niv_oxygen_connected_at || "",
          symptoms:
            data.niv_symptoms_improved === 1
              ? "Improved"
              : data.niv_symptoms_improved === 2
                ? "Persistent"
                : "",

          // Common Fields (Vitals & Consciousness)
          isConscious:
            data.tt_patient_conscious_unconscious === 1 ||
            data.niv_patient_conscious === 1
              ? "conscious/oriented"
              : data.tt_patient_conscious_unconscious === 2 ||
                  data.niv_patient_conscious === 2
                ? "Unconscious"
                : "",
          vitalsStable:
            data.tt_vitals_stable === 1 || data.niv_vitals_stable === 1
              ? "Yes"
              : data.tt_vitals_stable === 2 || data.niv_vitals_stable === 2
                ? "No"
                : "",

          // Exercises Fields
          deepBreathing:
            data.deep_breathing_exercises === 1
              ? "Yes"
              : data.deep_breathing_exercises === 2
                ? "No"
                : "",
          chestPhysio:
            data.chest_physiotherapy === 1
              ? "Yes"
              : data.chest_physiotherapy === 2
                ? "No"
                : "",
          spirometry:
            data.incentive_spirometry === 1
              ? "Yes"
              : data.incentive_spirometry === 2
                ? "No"
                : "",

          // MAP Remarks using TT OR NIV OR General specific keys
          deepBreathingRem:
            data.tt_deep_breathing_exercises_remark ||
            data.niv_deep_breathing_exercises_remark ||
            data.deep_breathing_exercises_remark ||
            "",
          chestPhysioRem:
            data.tt_chest_physiotherapy_remark ||
            data.niv_chest_physiotherapy_remark ||
            data.chest_physiotherapy_remark ||
            "",
          spirometryRem:
            data.tt_incentive_spirometry_remark ||
            data.niv_incentive_spirometry_remark ||
            data.incentive_spirometry_remark ||
            "",
        });

        if (data.closure_revalidate_remark) {
          setClosureRevalidateRemark(data.closure_revalidate_remark);
        }
      }
    }
  }, [sessions, evePlanID, profSD, profED, profST, profET]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.startDate) newErrors.startDate = "Start Date Required";
    if (!formData.startTime) newErrors.startTime = "Start Time Required";
    if (!formData.endDate) newErrors.endDate = "End Date Required";
    if (!formData.endTime) newErrors.endTime = "End Time Required";

    if (!closureRevalidateRemark.trim()) {
      newErrors.closureRevalidateRemark =
        "Closure Revalidate Remark is required";
    }

    if (
      formData.startDate &&
      formData.startTime &&
      formData.endDate &&
      formData.endTime
    ) {
      const startDateTime = new Date(
        `${formData.startDate}T${formData.startTime}`,
      );
      const endDateTime = new Date(`${formData.endDate}T${formData.endTime}`);

      if (endDateTime <= startDateTime) {
        newErrors.endTime = "Must be after Start Time";
        newErrors.endDate = "Invalid Date";
      }
    }

    setErrors(newErrors);

    if (!formData.patientStatus) {
      setSnackbarMessage("Please select Patient Status (TT, NIV or None)");
      setSeverity("warning");
      setOpenSnackbar(true);
      return false;
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    const requestData = {
      srv_prof_id: emt ? parseInt(emt) : 0,
      dtl_eve_id: parseInt(evePlanID),
      prof_sub_srv_id: 13,
      form_number: formNo,
      prof_st_dt: formData.startDate,
      prof_ed_dt: formData.endDate,
      prof_st_time:
        formData.startTime.length === 5
          ? `${formData.startTime}:00`
          : formData.startTime,
      prof_ed_time:
        formData.endTime.length === 5
          ? `${formData.endTime}:00`
          : formData.endTime,

      // TT Data
      tt_tracheostomy_patient: formData.patientStatus === "TT" ? 1 : 2,
      tt_patient_conscious_unconscious:
        formData.isConscious === "conscious/oriented" ? 1 : 2,
      tt_vitals_stable: formData.vitalsStable === "Yes" ? 1 : 2,
      tt_secretions:
        formData.secretions === "Mild"
          ? 1
          : formData.secretions === "Moderate"
            ? 2
            : 3,
      tt_stoma:
        formData.stomaStatus === "Healthy"
          ? 1
          : formData.stomaStatus === "Redness"
            ? 2
            : 3,
      tt_tube_remark: parseInt(formData.ttSize) || 0,
      tt_tube_cuff: formData.ttSecured === "Yes" ? 1 : 2,
      tt_tube_cuff_pressure_checked: formData.cuffPressure === "Yes" ? 1 : 2,
      tt_suction_system_working: formData.suctionWorking === "Yes" ? 1 : 2,
      tt_deep_breathing_exercises: formData.deepBreathing === "Yes" ? 1 : 2,
      tt_chest_physiotherapy: formData.chestPhysio === "Yes" ? 1 : 2,
      tt_incentive_spirometry: formData.spirometry === "Yes" ? 1 : 2,

      // NIV Data
      niv_patient_on_niv: formData.patientStatus === "NIV" ? 1 : 2,
      niv_patient_conscious:
        formData.isConscious === "conscious/oriented" ? 1 : 2,
      niv_vitals_stable: formData.vitalsStable === "Yes" ? 1 : 2,
      niv_mode: formData.nivMode === "CPAP" ? 1 : 2,
      niv_mask_fitting: formData.maskFitting === "Adequate" ? 1 : 2,
      niv_skin_over_nasal_bridge_intact_noted:
        formData.skinIntact === "Yes" ? 1 : 2,
      niv_compliance_hours: parseInt(formData.complianceHrs) || 0,
      niv_humidifier_functioning: formData.humidifier === "Yes" ? 1 : 2,
      niv_filter_clean: formData.filterClean === "Yes" ? 1 : 2,
      niv_oxygen_connected_at: parseInt(formData.oxygenLtr) || 0,
      niv_symptoms_improved: formData.symptoms === "Improved" ? 1 : 2,
      niv_deep_breath: formData.deepBreathing === "Yes" ? 1 : 2,
      niv_chest_physiotherapy: formData.chestPhysio === "Yes" ? 1 : 2,
      niv_incentive_spirometry: formData.spirometry === "Yes" ? 1 : 2,

      // General Exercises Data
      deep_breathing_exercises: formData.deepBreathing === "Yes" ? 1 : 2,
      chest_physiotherapy: formData.chestPhysio === "Yes" ? 1 : 2,
      incentive_spirometry: formData.spirometry === "Yes" ? 1 : 2,

      // 👇 ADDED SPECIFIC REMARKS KEYS AS REQUESTED

      // TT Remarks
      tt_deep_breathing_exercises_remark: formData.deepBreathingRem,
      tt_chest_physiotherapy_remark: formData.chestPhysioRem,
      tt_incentive_spirometry_remark: formData.spirometryRem,

      // NIV Remarks
      niv_deep_breathing_exercises_remark: formData.deepBreathingRem,
      niv_chest_physiotherapy_remark: formData.chestPhysioRem,
      niv_incentive_spirometry_remark: formData.spirometryRem,

      // General Remarks
      deep_breathing_exercises_remark: formData.deepBreathingRem,
      chest_physiotherapy_remark: formData.chestPhysioRem,
      incentive_spirometry_remark: formData.spirometryRem,

      closure_revalidate_remark: closureRevalidateRemark,
      closure_revalidate: 1,
      last_modified_by: userID,
    };

    try {
      const response = await fetch(
        `${API_URL}/hr/agg_hhc_session_job_closure/?dtl_eve=${evePlanID}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        },
      );
      if (response.ok) {
        setSnackbarMessage("Closure submitted successfully!");
        setSeverity("success");
        setOpenSnackbar(true);
        setTimeout(() => {
          onClose();
          if (typeof emt === "function") emt();
        }, 2000);
      } else {
        const errorData = await response.json();
        setSnackbarMessage(errorData.msg || "Submission failed.");
        setSeverity("error");
        setOpenSnackbar(true);
      }
    } catch (e) {
      setSeverity("error");
      setSnackbarMessage("Connection Error");
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        p: 2,
        bgcolor: "#ffffff",
        width: "100%",
        maxWidth: "600px",
        margin: "0 auto",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", color: "#000", ml: 0.5 }}
        >
          CLOSURE FORM
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#9e9e9e", p: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          maxHeight: "65vh",
          overflowY: "auto",
          overflowX: "hidden",
          pr: 1,
          pl: 0.5,
          pt: 1,
          "&::-webkit-scrollbar": { width: "5px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "10px",
          },
        }}
      >
        <Grid container spacing={1.5} sx={{ mb: 1.5, mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={errors.startDate ? "Error" : "Start Date *"}
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={formData.startDate}
              onChange={(e) => handleInputChange("startDate", e.target.value)}
              error={!!errors.startDate}
              helperText={errors.startDate}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={errors.startTime ? "Error" : "Start Time *"}
              type="time"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={formData.startTime}
              onChange={(e) => handleInputChange("startTime", e.target.value)}
              error={!!errors.startTime}
              helperText={errors.startTime}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={errors.endDate ? "Error" : "End Date *"}
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={formData.endDate}
              onChange={(e) => handleInputChange("endDate", e.target.value)}
              error={!!errors.endDate}
              helperText={errors.endDate}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={errors.endTime ? "Error" : "End Time *"}
              type="time"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={formData.endTime}
              onChange={(e) => handleInputChange("endTime", e.target.value)}
              error={!!errors.endTime}
              helperText={errors.endTime}
            />
          </Grid>
        </Grid>

        <QuestionCard
          label="1. Patient Status"
          field="patientStatus"
          value={formData.patientStatus}
          onChange={handleInputChange}
          options={["TT", "NIV", "None"]}
        />

        {/* TT SECTION */}
        {formData.patientStatus === "TT" && (
          <Box sx={{ mt: 1 }}>
            <QuestionCard
              label="1. Tracheostomy (TT) Patient"
              field="tt_status"
              value={formData.tt_status}
              onChange={handleInputChange}
            />
            <QuestionCard
              label="2. Patient Condition"
              field="isConscious"
              value={formData.isConscious}
              onChange={handleInputChange}
              options={["conscious/oriented", "Unconscious"]}
            />
            <QuestionCard
              label="3. Vitals Stable"
              field="vitalsStable"
              value={formData.vitalsStable}
              onChange={handleInputChange}
            />
            <QuestionCard
              label="4. Secretions"
              field="secretions"
              value={formData.secretions}
              onChange={handleInputChange}
              options={["Mild", "Moderate", "Copious"]}
            />
            <QuestionCard
              label="5. Stoma"
              field="stomaStatus"
              value={formData.stomaStatus}
              onChange={handleInputChange}
              options={["Healthy", "Redness", "Discharge present"]}
            />
            <Box sx={{ mb: 1.5 }}>
              <TextField
                label="6. TT Tube Size (Number)"
                fullWidth
                size="small"
                type="number"
                value={formData.ttSize}
                onChange={(e) => handleInputChange("ttSize", e.target.value)}
              />
            </Box>
            <QuestionCard
              label="7. Secured Properly (Cuffed/Uncuffed)"
              field="ttSecured"
              value={formData.ttSecured}
              onChange={handleInputChange}
            />
            <QuestionCard
              label="8. Cuff Pressure Checked"
              field="cuffPressure"
              value={formData.cuffPressure}
              onChange={handleInputChange}
            />
            <QuestionCard
              label="9. Suction System Working"
              field="suctionWorking"
              value={formData.suctionWorking}
              onChange={handleInputChange}
            />
          </Box>
        )}

        {/* NIV SECTION */}
        {formData.patientStatus === "NIV" && (
          <Box sx={{ mt: 1 }}>
            <QuestionCard
              label="1. Patient on NIV"
              field="niv_on"
              value={formData.niv_on}
              onChange={handleInputChange}
            />
            <QuestionCard
              label="2. Patient Condition"
              field="isConscious"
              value={formData.isConscious}
              onChange={handleInputChange}
              options={["conscious/oriented", "Unconscious"]}
            />
            <QuestionCard
              label="3. Vitals Stable"
              field="vitalsStable"
              value={formData.vitalsStable}
              onChange={handleInputChange}
            />
            <QuestionCard
              label="4. NIV Mode"
              field="nivMode"
              value={formData.nivMode}
              onChange={handleInputChange}
              options={["CPAP", "BiPAP"]}
            />
            <QuestionCard
              label="5. Mask Fitting"
              field="maskFitting"
              value={formData.maskFitting}
              onChange={handleInputChange}
              options={["Adequate", "Leak present"]}
            />
            <QuestionCard
              label="6. Skin Over Nasal Bridge Intact"
              field="skinIntact"
              value={formData.skinIntact}
              onChange={handleInputChange}
            />
            <Box sx={{ mb: 1.5 }}>
              <TextField
                label="7. Compliance Hours (hrs/day)"
                type="number"
                fullWidth
                size="small"
                value={formData.complianceHrs}
                onChange={(e) =>
                  handleInputChange("complianceHrs", e.target.value)
                }
              />
            </Box>
            <QuestionCard
              label="8. Humidifier Functioning"
              field="humidifier"
              value={formData.humidifier}
              onChange={handleInputChange}
            />
            <QuestionCard
              label="9. Filter Clean"
              field="filterClean"
              value={formData.filterClean}
              onChange={handleInputChange}
            />
            <Box sx={{ mb: 1.5 }}>
              <TextField
                label="10. Oxygen Connected At (L/min)"
                type="number"
                fullWidth
                size="small"
                value={formData.oxygenLtr}
                onChange={(e) => handleInputChange("oxygenLtr", e.target.value)}
              />
            </Box>
            <QuestionCard
              label="11. Symptoms"
              field="symptoms"
              value={formData.symptoms}
              onChange={handleInputChange}
              options={["Improved", "Persistent"]}
            />
          </Box>
        )}

        {/* EXERCISES SECTION */}
        <Typography
          variant="body1"
          sx={{ fontWeight: "bold", mb: 1.5, mt: 1.5, color: "#000" }}
        >
          Respiratory Exercises
        </Typography>
        {["deepBreathing", "chestPhysio", "spirometry"].map((key, index) => (
          <QuestionCard
            key={key}
            label={`${index + 1}. ${key === "deepBreathing" ? "Deep Breathing" : key === "chestPhysio" ? "Chest Physio" : "Incentive Spirometry"}`}
            field={key}
            value={formData[key]}
            onChange={handleInputChange}
          >
            {formData[key] === "No" && (
              <TextField
                label="Remark for reason"
                fullWidth
                size="small"
                sx={{ mt: 1.5 }}
                value={formData[`${key}Rem`] || ""}
                onChange={(e) => handleInputChange(`${key}Rem`, e.target.value)}
              />
            )}
          </QuestionCard>
        ))}

        {/* CLOSURE REVALIDATE REMARK FIELD */}
        <Grid item xs={12} sx={{ mt: 1, mb: 1.5 }}>
          <TextField
            id="closure_revalidate_remark"
            name="closure_revalidate_remark"
            label="Closure Revalidate Remark *"
            variant="outlined"
            multiline
            maxRows={4}
            rows={2}
            fullWidth
            value={closureRevalidateRemark}
            onChange={(e) => {
              setClosureRevalidateRemark(e.target.value);
              if (errors.closureRevalidateRemark) {
                setErrors((prev) => ({
                  ...prev,
                  closureRevalidateRemark: null,
                }));
              }
            }}
            error={!!errors.closureRevalidateRemark}
            helperText={errors.closureRevalidateRemark}
          />
        </Grid>
      </Box>

      {/* Submit Button Section */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2, pb: 0.5 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          sx={{
            bgcolor: "#2CDFAA",
            color: "#fff",
            px: 6,
            py: 1.2,
            minWidth: "220px",
            fontSize: "0.95rem",
            fontWeight: "bold",
            borderRadius: "6px",
            boxShadow: "none",
            "&:hover": { bgcolor: "#24b88d", boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "#85eed0", color: "#ffffff" },
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "SUBMIT"
          )}
        </Button>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={severity} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Closuretwelve;
